import React, { useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import patientService from '../../services/patientService';

export const patientName = (p) => [p.title, p.first_names, p.surname].filter(Boolean).join(' ');

export const ageText = (dob) => {
  if (!dob) return '';
  const years = (Date.now() - new Date(`${dob}T00:00:00`).getTime()) / (365.25 * 86400000);
  return years < 1 ? `${Math.max(0, Math.round(years * 12))} mo` : `${Math.floor(years)} y`;
};

/**
 * PatientSearch — type a name, ID number, member number or cell; pick a patient.
 * onPick(patient) gets the search row (name, DOB, sex, payer, scheme, membership).
 */
const PatientSearch = ({ onPick, exclude = [], disabled = false, placeholder = 'Add a patient — name, ID or member number…' }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    const text = q.trim();
    if (text.length < 2) { setResults([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const rows = await patientService.search(text);
        if (!cancelled) { setResults(rows.filter((p) => !exclude.includes(p.patient_id)).slice(0, 12)); setActive(0); setOpen(true); }
      } catch {
        if (!cancelled) setResults([]);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
    // exclude is compared by value on purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, exclude.join(',')]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const pick = (p) => {
    onPick(p);
    setQ('');
    setResults([]);
    setOpen(false);
  };

  const onKey = (e) => {
    if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); return; }
    if (!open || !results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === 'Enter') { e.preventDefault(); pick(results[active]); }
  };

  return (
    <div ref={boxRef} className="relative">
      <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onKeyDown={onKey}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full rounded-lg border border-slate-300 py-2 pl-8 pr-3 text-sm outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-teal/40 disabled:bg-slate-50"
      />
      {open && q.trim().length >= 2 && (
        <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {results.map((p, i) => (
            <li key={p.patient_id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(p)}
                onMouseEnter={() => setActive(i)}
                className={`flex w-full items-start gap-2 px-3 py-1.5 text-left text-sm ${i === active ? 'bg-teal-50' : ''}`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-slate-800">{patientName(p)}</span>
                  <span className="block text-[11px] text-slate-500">
                    {[ageText(p.date_of_birth), p.sex, p.payer === 'private' ? 'Private' : p.scheme_name && `${p.scheme_name} ${p.membership_number || ''}`.trim()].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {p.review_status === 'pending_review' && <span className="rounded bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-800">awaiting review</span>}
              </button>
            </li>
          ))}
          {results.length === 0 && <li className="px-3 py-2 text-xs text-slate-400">No matching patients. Add them on the Patients page.</li>}
        </ul>
      )}
    </div>
  );
};

export default PatientSearch;
